import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import ReportSummaryTab from "./ReportSummaryTab";
import InherentRiskTab from "./InherentRiskTab";
import ControlEfficacyTab from "./ControlEfficacyTab";
import SectionRiskScoreTab from "./SectionRiskScoreTab";
import OverallMineScoreTab from "./OverallMineScoreTab";
import LossAndRecommendationsTab from "./LossAndRecommendationsTab";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ReportValuesLayout = ({
    reportName,
    activeTab,
    onTabChange,
    onUpdate,
    isSaving,
    isLoadingValues,
    backLink,
    inherentRiskTabRef,
    inherentRiskTabProps,
    controlEfficacyTabRef,
    controlEfficacyTabProps,
    sectionRiskScoreTabProps,
    lossRows,
    setLossRows,
    recommendationRows,
    setRecommendationRows,
    onLossRowRemoved,
}) => {
    const tabItems = useMemo(
        () => [
            { value: "report-summary", label: "Report Summary" },
            { value: "inherent-risk", label: "Inherent Risk" },
            { value: "control-efficacy", label: "Control Efficacy" },
            { value: "section-risk-score", label: "Section Risk Score" },
            { value: "overall-mine-score", label: "Overall Mine Score" },
            { value: "loss-and-recommendations", label: "Loss & Recommendations" },
        ],
        []
    )

    const scrollContainerRef = useRef(null)
    const [isCompactLayout, setIsCompactLayout] = useState(false)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const updateScrollState = () => {
        const container = scrollContainerRef.current
        if (!container) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }

        const { scrollLeft, scrollWidth, clientWidth } = container
        const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0)
        setCanScrollLeft(scrollLeft > 4)
        setCanScrollRight(maxScrollLeft - scrollLeft > 4)
    }

    const handleScrollButton = () => {
        const container = scrollContainerRef.current
        if (!container) return

        const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0)
        const direction = canScrollRight ? "right" : "left"
        const targetLeft = direction === "right" ? maxScrollLeft : 0

        container.scrollTo({ left: targetLeft, behavior: "smooth" })
        setTimeout(updateScrollState, 320)
    }

    useEffect(() => {
        const handleResize = () => {
            const compact = window.innerWidth < 1280
            setIsCompactLayout(compact)
        }
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    useEffect(() => {
        updateScrollState()
    }, [tabItems, isCompactLayout])

    return (
        <>
            <div className="">
                <div className="py-4 slide-up">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-col gap-1 text-sm font-medium text-gray-0">
                            {backLink === "/field-engineer-reports" && (
                                <div className="flex items-center gap-2">
                                    <Link
                                        className="text-red-600 text-sm font-normal uppercase"
                                        to={backLink}
                                    >
                                        Back to Report
                                    </Link>
                                </div>
                            )}
                            <p>
                                <span className="uppercase">Edit Values for Report : </span> <span className="font-bold">{reportName || "Loading..."}</span></p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col w-full min-w-0 gap-6 slide-up">
                <Tabs value={activeTab} onValueChange={onTabChange} className="min-w-0">
                    <div className="sticky top-0 z-30 flex w-full min-w-0 bg-navy-600">
                        <div className={`flex items-center w-full gap-2 ${isCompactLayout ? "p-1 bg-navy-100 rounded-2xl" : "px-0 py-0 bg-transparent"}`}>
                            <div
                                ref={scrollContainerRef}
                                onScroll={updateScrollState}
                                className="flex flex-1 gap-2 p-0 overflow-x-auto no-scrollbar"
                            >
                                <TabsList className={`flex gap-2 min-w-max ${isCompactLayout ? "bg-transparent" : "px-2 py-6 bg-navy-100"}`}>
                                    {tabItems.map((tab) => (
                                        <TabsTrigger
                                            key={tab.value}
                                            value={tab.value}
                                            className={`p-2 !rounded-xl text-gray-200 bg-navy-300/20 text-sm font-medium transition whitespace-nowrap ${isCompactLayout ? " data-[state=active]:bg-red-500 data-[state=active]:text-gray-0" : "text-gray-0 data-[state=active]:bg-red-500 data-[state=active]:text-gray-0 rounded-none text-sm"}`}
                                        >
                                            {tab.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>
                            {(canScrollLeft || canScrollRight) && (
                                <button
                                    type="button"
                                    onClick={handleScrollButton}
                                    className="flex items-center justify-center w-8 h-8 bg-red-500 rounded-full shadow text-gray-0 hover:bg-red-600"
                                    aria-label={canScrollRight ? "Scroll tabs right" : "Scroll tabs left"}
                                >
                                    {canScrollRight ? (
                                        <ChevronRight className="w-4 h-4" />
                                    ) : (
                                        <ChevronLeft className="w-4 h-4" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    <TabsContent value="report-summary">
                        <ReportSummaryTab
                            inherentRiskRows={inherentRiskTabProps?.rows}
                            controlEfficacyRows={controlEfficacyTabProps?.rows}
                            sectionRiskRows={sectionRiskScoreTabProps?.rows}
                        />
                    </TabsContent>
                    <TabsContent value="inherent-risk">
                        <InherentRiskTab
                            ref={inherentRiskTabRef}
                            {...inherentRiskTabProps}
                        />
                    </TabsContent>
                    <TabsContent value="control-efficacy">
                        <ControlEfficacyTab
                            ref={controlEfficacyTabRef}
                            {...controlEfficacyTabProps}
                        />
                    </TabsContent>
                    <TabsContent value="section-risk-score">
                        <SectionRiskScoreTab
                            {...sectionRiskScoreTabProps}
                        />
                    </TabsContent>
                    <TabsContent value="overall-mine-score">
                        <OverallMineScoreTab sectionRiskRows={sectionRiskScoreTabProps?.rows} />
                    </TabsContent>
                    <TabsContent value="loss-and-recommendations">
                        <LossAndRecommendationsTab
                            lossRows={lossRows}
                            setLossRows={setLossRows}
                            recommendationRows={recommendationRows}
                            setRecommendationRows={setRecommendationRows}
                            onLossRowRemoved={onLossRowRemoved}
                        />
                    </TabsContent>
                </Tabs>
            </div>
            <div className="flex justify-end mt-2 border-t border-b border-gray-0">
                <div className="flex items-center justify-center w-auto">
                    <Link className="border-r rounded-b-none border-gray-0" to={backLink}>
                        <Button
                            variant="secondary"
                            className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                            Back
                        </Button>
                    </Link>
                    <Button
                        className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                        onClick={() => onUpdate({ lossRows, recommendationRows })}
                        disabled={isSaving || isLoadingValues}
                    >
                        {isSaving ? "Saving..." : "Update"}
                    </Button>
                </div>
            </div>
        </>
    )
}

export default ReportValuesLayout;

